// Thin fetch wrapper. Talks to the Express API mounted at /api. Honors the
// X-Org-Slug header so the API resolves operating org without us passing
// it everywhere. Throws an ApiError on non-2xx so TanStack Query can
// surface the message.
//
// Auth uses the session cookie set by /api/auth/google. credentials:'include'
// ensures the cookie rides along even cross-origin (relevant for the Vite
// dev proxy, no-op in prod where web + api are same-origin).

export class ApiError extends Error {
  status: number;
  code: string;
  details: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

// Seed from localStorage at module load so the org header is present
// on the very first request of ANY entry point — not just routes that
// mount <Layout> (which is what set this in-memory value before). The
// scorer (/score/:shareCode) lives outside the dashboard shell, so a
// dormant PWA that the OS discards and reopens directly on the scorer
// used to boot with activeOrgSlug=null → org-scoped calls like
// addFrame omitted X-Org-Slug → the server threw org_required.
// <Layout> still validates/overrides this against /me when it mounts.
function readStoredOrgSlug(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return window.localStorage.getItem("activeOrgSlug");
  } catch {
    // Private mode / sandboxed iframe can throw on access. This runs
    // at module init, so swallow rather than crash every importer.
    return null;
  }
}
let activeOrgSlug: string | null = readStoredOrgSlug();
export function setActiveOrgSlug(slug: string | null) {
  activeOrgSlug = slug;
}
export function getActiveOrgSlug(): string | null {
  return activeOrgSlug;
}

export type ApiFetchInit = Omit<RequestInit, "body" | "headers"> & {
  body?: unknown;
  headers?: Record<string, string>;
  // When true, skip attaching auth-context headers like X-Org-Slug. Public
  // endpoints (/g/:code, /auth/google, /healthz) use this.
  public?: boolean;
};

export async function api<T = unknown>(
  path: string,
  init: ApiFetchInit = {},
): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers ?? {}),
  };
  if (!init.public && activeOrgSlug && !headers["X-Org-Slug"]) {
    headers["X-Org-Slug"] = activeOrgSlug;
  }
  const url = path.startsWith("/api") ? path : `/api${path}`;

  // Hard timeout so a request can never hang indefinitely. Without
  // this, a stale keepalive socket to an auto-stopped Fly machine (the
  // browser won't retry a non-idempotent POST on a fresh connection)
  // could leave a mutation pending forever. 30s comfortably covers a
  // cold-start machine wake (~7-8s) while still failing eventually on a
  // dead connection so the caller can surface an error and retry.
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 30_000);
  let res: Response;
  try {
    res = await fetch(url, {
      ...init,
      headers,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
      credentials: "include",
      signal: controller.signal,
    });
  } catch (e) {
    if (e instanceof DOMException && e.name === "AbortError") {
      throw new ApiError(
        0,
        "request_timeout",
        "The request timed out. Check your connection and try again.",
      );
    }
    throw e;
  } finally {
    clearTimeout(timeout);
  }
  if (!res.ok) {
    let payload: { error?: string; message?: string; details?: unknown } = {};
    try {
      payload = await res.json();
    } catch {
      // not JSON
    }
    throw new ApiError(
      res.status,
      payload.error ?? `http_${res.status}`,
      payload.message ?? res.statusText,
      payload.details,
    );
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
